import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import AdminReconciliationProjectPage from "./AdminReconciliationProjectPage";
import "../reconciliation-dashboard.css";
import {
  fetchReconciliationRuns,
  fetchReconciliationSummary,
  triggerReconciliationJob,
} from "../services/reconciliationService";
import { downloadReconciliationProjectsCsv } from "../utils/reconciliationExport";
import { KpiCard, STATUS, StatusBadge } from "./reconciliationShared";

function ReconciliationListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [runs, setRuns] = useState([]);
  const [jobBusy, setJobBusy] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);
  const [jobOpts, setJobOpts] = useState({
    auto_backfill: true,
    fix_shares_sold: true,
    fix_cap_table: true,
    send_email: false,
    email_only_on_issues: true,
  });
  const [jobMsg, setJobMsg] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMsg, setExportMsg] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [lifecycleFilter, setLifecycleFilter] = useState("ALL");
  const [issueFilter, setIssueFilter] = useState("ALL");
  const [runDateFrom, setRunDateFrom] = useState("");
  const [runDateTo, setRunDateTo] = useState("");
  const jobAccordionRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await fetchReconciliationSummary({
        only_issues: onlyIssues ? "true" : "false",
      });
      setSummary(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load reconciliation summary.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [onlyIssues]);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await fetchReconciliationRuns();
      setRuns(Array.isArray(data) ? data : data?.results || []);
    } catch {
      setRuns([]);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadRuns();
  }, [loadSummary, loadRuns]);

  useEffect(() => {
    if (location.state?.openJob) {
      setJobOpen(true);
      jobAccordionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const runScheduledJob = async () => {
    setJobBusy(true);
    setJobMsg("");
    setError("");
    try {
      const { data } = await triggerReconciliationJob(jobOpts);
      setJobMsg(
        `Run ${data.status}: ${data.projects_with_issues} project(s) with issues` +
          (data.fund_ledger_rows_created ? ` · ${data.fund_ledger_rows_created} ledger rows fixed` : "") +
          (data.email_sent ? " · Email sent" : data.email_error ? ` · ${data.email_error}` : "")
      );
      await Promise.all([loadSummary(), loadRuns()]);
    } catch (e) {
      setError(e.response?.data?.detail || "Reconciliation job failed.");
    } finally {
      setJobBusy(false);
    }
  };

  const filteredProjects = useMemo(() => {
    let list = summary?.projects || [];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          (p.project_code || "").toLowerCase().includes(q) ||
          (p.project_name || "").toLowerCase().includes(q) ||
          (p.project_id || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "ALL") {
      list = list.filter((p) => (p.overall_status || "").toUpperCase() === statusFilter);
    }
    if (lifecycleFilter !== "ALL") {
      list = list.filter((p) => (p.status || "").toUpperCase() === lifecycleFilter);
    }
    if (issueFilter !== "ALL") {
      list = list.filter((p) => (p.issue_codes || []).includes(issueFilter));
    }
    return list;
  }, [summary, search, statusFilter, lifecycleFilter, issueFilter]);

  const lifecycleOptions = useMemo(() => {
    const set = new Set((summary?.projects || []).map((p) => p.status).filter(Boolean));
    return ["ALL", ...Array.from(set).sort()];
  }, [summary]);

  const filteredRuns = useMemo(() => {
    return runs.filter((r) => {
      if (!r.started_at) return true;
      const d = new Date(r.started_at);
      if (runDateFrom && d < new Date(runDateFrom)) return false;
      if (runDateTo) {
        const end = new Date(runDateTo);
        end.setHours(23, 59, 59, 999);
        if (d > end) return false;
      }
      return true;
    });
  }, [runs, runDateFrom, runDateTo]);

  const stats = summary?.stats || {};
  const lastRun = runs[0];

  const openProject = (id) => {
    navigate(`/admin/reconciliation/projects/${id}`);
  };

  const exportProjectsCsv = () => {
    if (!filteredProjects.length) {
      setExportMsg("Nothing to export — adjust filters or refresh.");
      return;
    }
    setExportBusy(true);
    setExportMsg("");
    try {
      downloadReconciliationProjectsCsv(filteredProjects, { generatedAt: summary?.generated_at });
      setExportMsg(`Exported ${filteredProjects.length} project(s).`);
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <div className="recon-dash page">
      <header className="recon-dash__hero">
        <div>
          <h1>Financial reconciliation</h1>
          <p>
            Verify that system records, share ledgers, and project fund cash align. Select a project to open a full
            reconciliation workspace with fix steps and actions.
          </p>
          {summary?.generated_at ? (
            <div className="recon-dash__hero-meta">
              Last refreshed {new Date(summary.generated_at).toLocaleString()}
              {lastRun ? ` · Last job ${new Date(lastRun.started_at).toLocaleString()}` : ""}
            </div>
          ) : null}
        </div>
        <div className="recon-dash__actions">
          <button type="button" className="history-btn history-btn--secondary" onClick={loadSummary} disabled={loading}>
            Refresh data
          </button>
          <button
            type="button"
            className="history-btn history-btn--secondary"
            onClick={exportProjectsCsv}
            disabled={exportBusy || loading}
          >
            {exportBusy ? "Exporting…" : "Export CSV"}
          </button>
          <button type="button" className="history-btn history-btn--primary" onClick={runScheduledJob} disabled={jobBusy}>
            {jobBusy ? "Running…" : "Run reconciliation job"}
          </button>
        </div>
      </header>

      <div className="recon-legend">
        <span>
          <StatusBadge status={STATUS.MATCHED} small /> All checks pass
        </span>
        <span>
          <StatusBadge status={STATUS.PENDING_REVIEW} small /> Actionable fix available (e.g. backfill)
        </span>
        <span>
          <StatusBadge status={STATUS.MISMATCHED} small /> Click project for full investigation
        </span>
      </div>

      <div className="recon-kpi-grid">
        <KpiCard label="Projects scanned" value={summary?.project_count ?? "—"} hint="In current filter" variant="neutral" />
        <KpiCard label="Fully matched" value={stats.projects_matched ?? "—"} hint="No discrepancies" variant="matched" />
        <KpiCard
          label="Pending review"
          value={stats.projects_pending_review ?? "—"}
          hint="Usually missing ledger rows"
          variant="warn"
        />
        <KpiCard label="Mismatched" value={stats.projects_mismatched ?? "—"} hint="Needs investigation" variant="danger" />
        <KpiCard
          label="Missing ledger rows"
          value={stats.total_missing_ledger_rows ?? "—"}
          hint="Across all projects"
          variant="warn"
        />
        <KpiCard label="With any issue" value={summary?.projects_with_issues ?? "—"} hint="Any failed check" variant="danger" />
      </div>

      <div className="recon-filters">
        <label>
          Search project
          <input type="text" placeholder="Code or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label>
          Reconciliation status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All statuses</option>
            <option value={STATUS.MATCHED}>Matched</option>
            <option value={STATUS.PENDING_REVIEW}>Pending review</option>
            <option value={STATUS.MISMATCHED}>Mismatched</option>
          </select>
        </label>
        <label>
          Project lifecycle
          <select value={lifecycleFilter} onChange={(e) => setLifecycleFilter(e.target.value)}>
            {lifecycleOptions.map((o) => (
              <option key={o} value={o}>
                {o === "ALL" ? "All stages" : o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Issue type
          <select value={issueFilter} onChange={(e) => setIssueFilter(e.target.value)}>
            <option value="ALL">All issue types</option>
            <option value="missing_fund_ledger_entries">Missing fund ledger</option>
            <option value="fund_ledger_balance_mismatch">Fund ledger mismatch</option>
            <option value="shares_sold_mismatch">Shares sold mismatch</option>
            <option value="cap_table_mismatch">Cap table mismatch</option>
          </select>
        </label>
        <label className="history-filter-inline" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
          <span style={{ textTransform: "none", fontSize: 13, fontWeight: 500 }}>Load only projects with issues</span>
        </label>
      </div>

      {error ? <p className="form-error">{error}</p> : null}
      {exportMsg ? <p className="muted" style={{ marginBottom: 8 }}>{exportMsg}</p> : null}
      {jobMsg ? <p className="muted" style={{ marginBottom: 12 }}>{jobMsg}</p> : null}

      <div className="recon-list-panel recon-list-panel--full">
        <div className="recon-list-panel__head">
          <span>Projects ({filteredProjects.length})</span>
          <span className="muted small">Click a row to open full reconciliation</span>
        </div>
        {loading && !filteredProjects.length ? (
          <div className="recon-loading">Loading reconciliation data…</div>
        ) : filteredProjects.length === 0 ? (
          <div className="recon-loading">No projects match your filters.</div>
        ) : (
          filteredProjects.map((p) => (
            <div key={p.project_id} className="recon-project-row">
              <button type="button" className="recon-project-row__main" onClick={() => openProject(p.project_id)}>
                <div className="recon-project-row__info">
                  <div className="recon-project-row__code">{p.project_code || p.project_id.slice(0, 8)}</div>
                  <div className="recon-project-row__name">{p.project_name}</div>
                  {p.issue_labels?.length ? (
                    <div className="recon-issue-chips recon-issue-chips--inline">
                      {p.issue_labels.slice(0, 2).map((lbl) => (
                        <span key={lbl} className="recon-issue-chip">
                          {lbl}
                        </span>
                      ))}
                      {p.issue_labels.length > 2 ? (
                        <span className="recon-issue-chip recon-issue-chip--more">+{p.issue_labels.length - 2}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <span className="recon-project-row__lifecycle">{p.status}</span>
                <StatusBadge status={p.overall_status || (p.has_issues ? STATUS.MISMATCHED : STATUS.MATCHED)} />
                <span className="recon-project-row__chevron" aria-hidden>
                  →
                </span>
              </button>
            </div>
          ))
        )}
      </div>

      <div className="recon-accordion" ref={jobAccordionRef} id="recon-job-accordion">
        <button type="button" className="recon-accordion__trigger" onClick={() => setJobOpen((o) => !o)}>
          Automated reconciliation job & history
          <span>{jobOpen ? "▲" : "▼"}</span>
        </button>
        {jobOpen ? (
          <div className="recon-accordion__body">
            <p className="muted small" style={{ marginTop: 0 }}>
              Schedule via cron: <code>python manage.py run_reconciliation_job --cron --email --auto-backfill</code>
            </p>
            <div className="recon-filters" style={{ marginTop: 12, marginBottom: 16, padding: 12 }}>
              <label className="history-filter-inline" style={{ flexDirection: "row", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={jobOpts.auto_backfill}
                  onChange={(e) => setJobOpts((o) => ({ ...o, auto_backfill: e.target.checked }))}
                />
                <span style={{ textTransform: "none", fontWeight: 500 }}>Auto backfill</span>
              </label>
              <label className="history-filter-inline" style={{ flexDirection: "row", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={jobOpts.fix_shares_sold}
                  onChange={(e) => setJobOpts((o) => ({ ...o, fix_shares_sold: e.target.checked }))}
                />
                <span style={{ textTransform: "none", fontWeight: 500 }}>Fix shares_sold</span>
              </label>
              <label className="history-filter-inline" style={{ flexDirection: "row", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={jobOpts.fix_cap_table}
                  onChange={(e) => setJobOpts((o) => ({ ...o, fix_cap_table: e.target.checked }))}
                />
                <span style={{ textTransform: "none", fontWeight: 500 }}>Fix cap table (eligible only)</span>
              </label>
              <label className="history-filter-inline" style={{ flexDirection: "row", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={jobOpts.send_email}
                  onChange={(e) => setJobOpts((o) => ({ ...o, send_email: e.target.checked }))}
                />
                <span style={{ textTransform: "none", fontWeight: 500 }}>Email ops</span>
              </label>
              <label className="history-filter-inline" style={{ flexDirection: "row", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={jobOpts.email_only_on_issues}
                  onChange={(e) => setJobOpts((o) => ({ ...o, email_only_on_issues: e.target.checked }))}
                />
                <span style={{ textTransform: "none", fontWeight: 500 }}>Email only if issues</span>
              </label>
            </div>

            <div className="recon-filters" style={{ marginBottom: 16 }}>
              <label>
                Job history from
                <input type="date" value={runDateFrom} onChange={(e) => setRunDateFrom(e.target.value)} />
              </label>
              <label>
                To
                <input type="date" value={runDateTo} onChange={(e) => setRunDateTo(e.target.value)} />
              </label>
            </div>

            {filteredRuns.length > 0 ? (
              <table className="recon-txn-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Issues</th>
                    <th>Fixes</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRuns.slice(0, 15).map((r) => (
                    <tr key={r.id}>
                      <td>{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</td>
                      <td>
                        <StatusBadge
                          status={
                            r.status === "COMPLETED"
                              ? STATUS.MATCHED
                              : r.status === "FAILED"
                                ? STATUS.MISMATCHED
                                : STATUS.PENDING_REVIEW
                          }
                          small
                        />
                      </td>
                      <td>{r.trigger_source}</td>
                      <td>
                        {r.projects_with_issues} / {r.project_count}
                      </td>
                      <td>
                        {r.fund_ledger_rows_created || 0} ledger · {r.shares_sold_projects_fixed || 0} shares ·{" "}
                        {r.cap_table_projects_fixed || 0} cap
                      </td>
                      <td>{r.email_sent ? "Sent" : r.email_error || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted small">No job runs in this date range.</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminReconciliationPage() {
  return (
    <Routes>
      <Route index element={<ReconciliationListPage />} />
      <Route path="projects/:projectId" element={<AdminReconciliationProjectPage />} />
    </Routes>
  );
}
